import { Link } from "react-router-dom";
import { useLang } from "@/contexts/LanguageContext";
import { BRAND_CONTACT } from "@/data/products";
import { Instagram, Facebook, Mail, Phone, MapPin, MessageCircle } from "lucide-react";
import { useState } from "react";

const NAV_KEYS = ["inicio", "productos", "produccion", "logistica", "tienda", "eventos", "contacto"];

const Footer = () => {
  const { t } = useLang();
  const [email, setEmail] = useState("");
  const [subscribed, setSubscribed] = useState(false);

  const onSubscribe = (e) => {
    e.preventDefault();
    if (email) {
      setSubscribed(true);
      setEmail("");
      setTimeout(() => setSubscribed(false), 4000);
    }
  };

  return (
    <footer
      data-testid="site-footer"
      className="bg-wine text-bg px-6 md:px-12 lg:px-20 flex flex-col"
      style={{ minHeight: "300px" }}
    >
      <div className="max-w-[1500px] mx-auto w-full flex-1 flex flex-col justify-between py-7 md:py-8">
        {/* Top: 4 balanced columns */}
        <div className="grid grid-cols-2 md:grid-cols-12 gap-8 md:gap-10">
          {/* Brand */}
          <div className="col-span-2 md:col-span-4 flex flex-col">
            <Link to="/" data-testid="footer-logo-link" className="inline-block mb-4">
              <img
                src="/logo-criollita.png"
                alt="La Criollita"
                className="h-16 md:h-20 w-auto object-contain"
              />
            </Link>
            <p className="text-bone/80 font-light text-xs md:text-sm leading-relaxed max-w-xs whitespace-pre-line">
              {t.footer.tagline}
            </p>
            <div className="flex items-center gap-4 mt-auto pt-5">
              <a
                href={BRAND_CONTACT.instagram}
                target="_blank"
                rel="noreferrer"
                data-testid="footer-instagram"
                className="text-bone/70 hover:text-bg transition-colors"
                aria-label="Instagram"
              >
                <Instagram className="w-4 h-4" strokeWidth={1.5} />
              </a>
              <a
                href={BRAND_CONTACT.facebook}
                target="_blank"
                rel="noreferrer"
                data-testid="footer-facebook"
                className="text-bone/70 hover:text-bg transition-colors"
                aria-label="Facebook"
              >
                <Facebook className="w-4 h-4" strokeWidth={1.5} />
              </a>
              <a
                href={`https://wa.me/${BRAND_CONTACT.whatsapp}`}
                target="_blank"
                rel="noreferrer"
                data-testid="footer-whatsapp"
                className="text-bone/70 hover:text-bg transition-colors"
                aria-label="WhatsApp"
              >
                <MessageCircle className="w-4 h-4" strokeWidth={1.5} />
              </a>
            </div>
          </div>

          {/* Navigation */}
          <div className="col-span-1 md:col-span-3">
            <h4 className="text-[10px] uppercase tracking-[0.25em] text-caramel mb-4 font-medium">
              {t.footer.nav}
            </h4>
            <ul className="grid grid-cols-1 gap-2">
              {NAV_KEYS.map((key) => (
                <li key={key}>
                  <Link
                    data-testid={`footer-link-${key}`}
                    to={key === "inicio" ? "/" : `/${key}`}
                    className="text-xs text-bone/80 hover:text-bg transition-colors font-light"
                  >
                    {t.nav[key]}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div className="col-span-1 md:col-span-3">
            <h4 className="text-[10px] uppercase tracking-[0.25em] text-caramel mb-4 font-medium">
              {t.footer.contact}
            </h4>
            <ul className="space-y-2.5 text-xs text-bone/80 font-light">
              <li className="flex items-start gap-2">
                <MapPin className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-caramel" strokeWidth={1.5} />
                <span className="leading-snug">
                  Camí de Muntanya, 7 — 1º<br />
                  Pol. Ind. de Marratxí<br />
                  07141 Mallorca · España
                </span>
              </li>
              <li className="flex items-center gap-2">
                <Phone className="w-3.5 h-3.5 flex-shrink-0 text-caramel" strokeWidth={1.5} />
                <a href={`tel:${BRAND_CONTACT.phone}`} className="hover:text-bg">
                  {BRAND_CONTACT.phone}
                </a>
              </li>
              <li className="flex items-center gap-2">
                <Mail className="w-3.5 h-3.5 flex-shrink-0 text-caramel" strokeWidth={1.5} />
                <a
                  href={`mailto:${BRAND_CONTACT.email}`}
                  className="hover:text-bg break-all"
                >
                  {BRAND_CONTACT.email}
                </a>
              </li>
            </ul>
          </div>

          {/* Newsletter */}
          <div className="col-span-2 md:col-span-2">
            <h4 className="text-[10px] uppercase tracking-[0.25em] text-caramel mb-4 font-medium">
              {t.footer.newsletter}
            </h4>
            <form onSubmit={onSubscribe} data-testid="newsletter-form" className="flex flex-col gap-3">
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t.footer.placeholder}
                data-testid="newsletter-email-input"
                className="bg-transparent border-b border-bone/30 text-xs text-bg placeholder:text-bone/40 outline-none py-2 font-light focus:border-bg transition-colors"
              />
              <button
                type="submit"
                data-testid="newsletter-submit"
                className="self-start text-[10px] uppercase tracking-[0.2em] text-bg hover:text-caramel transition-colors border-b border-bg pb-0.5 font-medium"
              >
                {t.footer.subscribe} →
              </button>
            </form>
            {subscribed && (
              <p
                data-testid="newsletter-success"
                className="text-caramel text-xs mt-3 font-light"
              >
                ✓ {t.contact.sent}
              </p>
            )}
          </div>
        </div>

        {/* Bottom legal */}
        <div className="mt-5 pt-4 border-t border-bone/15 flex flex-col md:flex-row md:items-center md:justify-between gap-2 text-[10px] text-bone/55 font-light uppercase tracking-wider">
          <div className="flex items-center gap-3 flex-wrap">
            <span>© {new Date().getFullYear()} La Criollita</span>
            <span className="hidden md:inline text-bone/30">·</span>
            <span>Hecho con cariño en Mallorca</span>
          </div>
          <div className="flex gap-4">
            <a href="/politica-privacidad" className="hover:text-bg transition-colors">{t.footer.privacy}</a>
            <a href="/terminos-condiciones" className="hover:text-bg transition-colors">{t.footer.terms}</a>
            <a href="/politica-cookies" className="hover:text-bg transition-colors">{t.footer.cookies}</a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
